package screens.wallColourer

import com.github.ajalt.colormath.model.RGB
import common.EConstants
import common.ECssClasses
import components.AlpinaLogo
import components.ButtonContent
import components.SliderRow
import components.Spacer.Spacer
import components.Toolbar
import components.alert.Alert
import components.alert.AlertItem
import components.burgerMenu.BurgerMenu
import components.loadingSpinner.LoadingSpinner
import core.AppContext
import core.enums.EImages
import core.enums.EJSBridgeActions
import core.enums.ELocalizationKeys
import core.enums.EPostMessageActions
import core.extensions.isInNativeApp
import core.extensions.localized
import core.extensions.postMessageToNativeApp
import core.extensions.toClassName
import core.extensions.useCoroutineScope
import core.io.CustomHttpResponse
import core.io.dto.JSBridgeActionDTO
import core.io.dto.JSBridgeRGBColourDTO
import core.io.dto.MinifiedColourCollectionDTO
import core.ionic.IonButton
import core.ionic.IonCol
import core.ionic.IonContent
import core.ionic.IonGrid
import core.ionic.IonHeader
import core.ionic.IonPage
import core.ionic.IonRow
import core.localStorage.LocalStorageDAO
import core.localization.Localization
import core.piwik.Piwik
import core.upload.UploadHelper
import core.util.exhaustive
import csstype.Display
import csstype.JustifyContent
import csstype.TextAlign
import csstype.pct
import csstype.px
import emotion.react.css
import kotlinext.js.asJsObject
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import module.home.SliderObject
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event
import react.FC
import react.Props
import react.dom.html.InputType
import react.dom.html.ReactHTML
import react.router.Navigate
import react.router.useNavigate
import react.useContext
import react.useEffectOnce
import react.useRef
import react.useState
import screens.EScreens
import setUnsafeInnerHTML

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 03.11.22
 * Author : mariorosshirt
 */

external interface WallColourerUploadScreenProps : Props {
    var mToken: String?
}

@Suppress("TopLevelPropertyNaming")
val WallColourerUploadScreen = FC<WallColourerUploadScreenProps> { props ->

    val mContext by useContext(AppContext)
    var (mLocalStorage, mLocalStorageDispatch) = mContext.mLocalStorageReducer
    var mTargetScreen: String? by useState(null)
    var mToken: String? by useState(props.mToken)
    val mFileInputRef = useRef<HTMLInputElement>(null)
    var mIsLoading by useState(false)
    val mScope = useCoroutineScope()
    val mNavigate = useNavigate()
    var mAlertItem: AlertItem? by useState(null)

    val mSerializer = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    useEffectOnce {
        val eventMethod: (Event) -> Unit = { event ->
            event as MessageEvent
            val target = event.data?.asJsObject().unsafeCast<String>()
            val dto = kotlin.runCatching { mSerializer.decodeFromString<MinifiedColourCollectionDTO>(target) }.getOrNull()
            when (dto?.mAction) {
                EPostMessageActions.SET_FIXED_LIST -> {
                    mContext.mLocalStorageReducer.component2().invoke(LocalStorageDAO.ELocalStorageAction.CLEAR_FIXED_LIST)
                    mContext.mLocalStorageReducer.component2().invoke(LocalStorageDAO.ELocalStorageAction.CLEAR_SUGGESTED_LIST)
                    dto.mData?.forEach {
                        mContext.mLocalStorageReducer.component2().invoke(
                            LocalStorageDAO.ELocalStorageAction.ADD_OR_DELETE_FROM_FIXEDLIST(
                                it
                            )
                        )
                    }
                }
                null -> {}
                else -> {}
            }.exhaustive
        }

        window.addEventListener(type = "message", eventMethod)
        cleanup {
            window.removeEventListener(type = "message", eventMethod)
        }
    }

    BurgerMenu()
    IonPage {
        id = EConstants.BURGER_MENU_CONTENT.mId
        IonHeader {
            Toolbar()
            AlpinaLogo()
        }
        IonContent {
            IonGrid {
                css(
                    classNames = arrayOf(
                        ECssClasses.VERTICAL_GRID.toClassName(),
                        ECssClasses.NON_SCROLLABLE_GRID.toClassName()
                    )
                ) {}
                SliderRow() {
                    mObjectList = listOf(SliderObject("", EImages.HEADER_IMAGE_S06.mRelativePath, 0))
                    mSliderIterator = 0
                }
                Spacer()
                IonRow {
                    IonCol {
                        size = "12"
                        css(ECssClasses.TITLE_FONT.toClassName()) {
                            textAlign = TextAlign.center
                            marginBottom = 24.px
                        }
                        ReactHTML.span {
                            +Localization.getLocalizationKey(ELocalizationKeys.S06_TITLE_1.name)
                        }
                    }
                }
                IonRow {
                    IonCol {
                        size = "12"
                        css(ECssClasses.BODY_FONT.toClassName()) {
                            textAlign = TextAlign.center
                        }
                        ReactHTML.span {
                            setUnsafeInnerHTML(Localization.getLocalizationKey(ELocalizationKeys.S06_TEXT_1.name))
                        }
                    }
                }
                Spacer()
                IonRow {
                    IonCol {
                        size = "12"
                        sizeSm = "9"
                        sizeMd = "8"
                        offset = "0"
                        offsetSm = "1.5"
                        offsetMd = "2"
                        IonButton {
                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                width = 100.pct
                            }
                            onClick = {
                                mFileInputRef.current?.click()
                            }
                            ButtonContent {
                                mLeftImage = EImages.CAMERA_ICON
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.S02_BUTTON_CONTENT_TAKE_PICTURE.name)
                            }
                        }
                    }
                    ReactHTML.input {
                        type = InputType.file
                        name = "selectFile"
                        ref = mFileInputRef
                        hidden = true
                        accept = UploadHelper.getAcceptedFileTypes()
                        onChange = {
                            mScope.launch {
                                val itemToUpload = it.target.files?.item(0) ?: return@launch

                                mIsLoading = true
                                val response = UploadHelper.handleFileInputChange(itemToUpload, 1)
                                when (response) {
                                    is CustomHttpResponse.Success ->
                                        mToken = response.data
                                    is CustomHttpResponse.Error.NoInternet<*> ->
                                        mAlertItem = AlertItem.okAlertItem(
                                            ELocalizationKeys.ALERT_MESSAGE_NO_INTERNET_CONNECTION.localized,
                                            )
                                    is CustomHttpResponse.Error.Timeout<*> ->
                                        mAlertItem = AlertItem.okAlertItem(
                                            ELocalizationKeys.ALERT_MESSAGE_CONNECT_TIMEOUT.localized,
                                            )
                                    is CustomHttpResponse.Error.Unknown<*> ->
                                        Unit
                                }
                                if (mToken == null) mIsLoading = false

                                mFileInputRef.current?.value = ""
                            }
                        }
                    }
                }

                IonRow {
                    IonCol {
                        size = "12"
                        sizeSm = "9"
                        sizeMd = "8"
                        offset = "0"
                        offsetSm = "1.5"
                        offsetMd = "2"
                        IonButton {
                            css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                width = 100.pct
                            }
                            onClick = {
                                mTargetScreen = EScreens.GALLERY_SCREEN.mPath
                            }
                            ButtonContent {
                                mLeftImage = EImages.SECOND_PRIMARY_BUTTON_IMAGE
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.S06_BUTTON_CONTENT_EXAMPLEROOMS.name)
                            }
                        }
                    }
                }

                if(window.isInNativeApp()) {
                    IonRow {
                        css {
                            /*
                            if (!window.isInNativeApp()){
                                display = Display.none
                            }
                             */
                        }
                        IonCol {
                            size = "12"
                            sizeSm = "9"
                            sizeMd = "8"
                            offset = "0"
                            offsetSm = "1.5"
                            offsetMd = "2"
                            IonButton {
                                css(ECssClasses.PRIMARY_BUTTON.toClassName()) {
                                    width = 100.pct
                                }
                                onClick = {
                                    val colourList = mLocalStorage.mFixedList.map {
                                        JSBridgeRGBColourDTO(
                                            RGB(it.mColourCode).redInt.toString(),
                                            RGB(it.mColourCode).greenInt.toString(),
                                            RGB(it.mColourCode).blueInt.toString(),
                                            it.mUnique
                                        )
                                    }.toMutableList()
                                    colourList += mLocalStorage.mSuggestedList.map {
                                        JSBridgeRGBColourDTO(
                                            RGB(it.mColourCode).redInt.toString(),
                                            RGB(it.mColourCode).greenInt.toString(),
                                            RGB(it.mColourCode).blueInt.toString(),
                                            it.mUnique
                                        )
                                    }
                                    val jsBridgeActionDTO = JSBridgeActionDTO(
                                        EJSBridgeActions.SHOW_VIDEO,
                                        colourList
                                    )
                                    window.postMessageToNativeApp(jsBridgeActionDTO)

                                    Piwik.trackCustomEvent(
                                        Piwik.CATEGORY_LIVE_VIEW,
                                        Piwik.NAME_LIVE_VIEW_OPEN,
                                        Piwik.NAME_LIVE_VIEW_OPEN
                                    )
                                }
                                ButtonContent {
                                    mLeftImage = EImages.COLOUR_ON_WALL_ICON
                                    mContent =
                                        Localization.getLocalizationKey(ELocalizationKeys.SHOW_LIVE_BUTTON.name)
                                }
                            }
                        }
                    }
                }
                IonRow {
                    css(ECssClasses.BOTTOM_BUTTONS.toClassName()) {}
                    IonCol {
                        css {
                            justifyContent = JustifyContent.spaceBetween
                            display = Display.flex
                            padding = 0.px
                        }
                        IonButton {
                            css(ECssClasses.SECONDARY_BUTTON.toClassName()) {
                                width = 165.px
                            }
                            onClick = {
                                if(window.history.state != null)
                                    mNavigate.invoke(-1)
                                else{
                                    mTargetScreen = EScreens.COLOUR_TONE_LIST_SCREEN.mPath
                                }
                            }
                            ButtonContent {
                                mContent =
                                    Localization.getLocalizationKey(ELocalizationKeys.CORE_BACK.name)
                            }
                        }
                    }
                }
            }
        }

        if (mIsLoading) {
            LoadingSpinner()
        }

        mToken?.let {
            Navigate {
                to = EScreens.WALL_COLOURER_MODULE_SCREEN(EScreens.EColoursOnWallType.TOKEN, mToken = it).getPath()
            }
        }
        mTargetScreen?.let {
            Navigate {
                to = it
            }
        }
        mAlertItem?.let {
            Alert {
                this.mAlertItem = it
            }
        }
    }
}